import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import MedCalendar from '@/pages/med/MedCalendar';
import MedDailyView from '@/pages/med/MedDailyView';
import MedDayEditModal from '@/pages/med/MedDayEditModal';
import MedNotesModal from '@/pages/med/MedNotesModal';
import {
  fetchMedDays,
  upsertMedDay,
  emptyDay,
  logicalTodayIso,
  nowEasternPretty,
  nowEasternDatePretty,
  isFullyTaken,
  migrateFreeze,
  readBackup,
  writeBackup,
  clearBackup,
  keepSessionAlive,
} from '@/pages/med/medUtils';

// Private page — twylightcrow.com/med
// - Not linked from the main site
// - Excluded from robots.txt
// - Google login only
// - Only `silverfirestar14@gmail.com` or an app admin may see the content
const ALLOWED_EMAIL = 'silverfirestar14@gmail.com';

export default function MedTaken() {
  const { user, loading, logout, realUser, isRealAdmin } = useAuth();

  // Extra safety: prevent search engines from indexing this route
  // even if they somehow bypass robots.txt.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'MedTrack';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive, nosnippet';
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      try { document.head.removeChild(meta); } catch (_e) { /* noop */ }
    };
  }, []);

  const email = (user?.email || realUser?.email || '').toLowerCase();
  const isAllowed = useMemo(() => {
    if (!email) return false;
    if (email === ALLOWED_EMAIL) return true;
    if (isRealAdmin) return true; // Admin (site owner) always allowed to view/fix
    return false;
  }, [email, isRealAdmin]);

  const handleGoogleLogin = () => {
    // Emergent Google OAuth flow. Redirect back to this same page so
    // AuthCallback can drop us on /med after the session is established.
    // REMINDER: DO NOT HARDCODE THE URL OR ADD FALLBACKS — this breaks auth.
    const redirectUrl = window.location.origin + '/med';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div
        className="min-h-screen min-h-[100svh] flex items-center justify-center bg-white text-slate-900"
        data-testid="med-loading"
      >
        <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className="min-h-screen min-h-[100svh] flex items-center justify-center bg-white text-slate-900 px-6"
        data-testid="med-signin-screen"
      >
        <div className="w-full max-w-xs space-y-6 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">MedTrack</h1>
          <Button
            variant="outline"
            className="w-full h-11 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50"
            onClick={handleGoogleLogin}
            data-testid="med-google-login-button"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>
        </div>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div
        className="min-h-screen min-h-[100svh] flex items-center justify-center bg-white text-slate-900 px-6"
        data-testid="med-denied-screen"
      >
        <div className="w-full max-w-xs space-y-4 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">MedTrack</h1>
          <p className="text-sm text-slate-600" data-testid="med-denied-message">
            This page isn't available for your account.
          </p>
          <Button
            variant="outline"
            className="w-full h-10 bg-white border border-slate-300 text-slate-900 hover:bg-slate-50"
            onClick={logout}
            data-testid="med-signout-button"
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return <MedApp onSignOut={logout} user={user} onSignInAgain={handleGoogleLogin} />;
}

// -----------------------------------------------------------------------------
// MedApp — Tabs: Calendar / Daily View + shared day state
// -----------------------------------------------------------------------------
function MedApp({ onSignOut, user, onSignInAgain }) {
  const [params, setParams] = useSearchParams();
  const viewParam = params.get('view');
  const initialView =
    viewParam === 'calendar' ? 'calendar'
    : viewParam === 'settings' ? 'settings'
    : 'daily';
  const [view, setView] = useState(initialView);

  // The current MedTrack "logical" date (rolls over at 4 AM ET, per
  // owner spec — see medUtils.logicalTodayIso). The wall-clock label
  // still shows real ET time. Refreshed on a short interval so the
  // 4 AM day-flip lands automatically without a page reload.
  const [todayIso, setTodayIso] = useState(() => logicalTodayIso());
  const [nowPretty, setNowPretty] = useState(() => nowEasternPretty());
  const [datePretty, setDatePretty] = useState(() => nowEasternDatePretty());
  useEffect(() => {
    const tick = () => {
      setTodayIso(logicalTodayIso());
      setNowPretty(nowEasternPretty());
      setDatePretty(nowEasternDatePretty());
    };
    const id = setInterval(tick, 15000); // 15 s is plenty for a minute clock
    return () => clearInterval(id);
  }, []);

  // Which month the Calendar is showing. Defaults to the month of today.
  const [{ year, month }, setYm] = useState(() => {
    const [y, m] = todayIso.split('-').map(Number);
    return { year: y, month: m - 1 };
  });

  // Day state cache keyed by ISO date.
  const [days, setDays] = useState({}); // { [iso]: dayDoc }
  const daysRef = useRef({});
  daysRef.current = days;
  const [modalDate, setModalDate] = useState(null); // ISO string or null
  const [notesDate, setNotesDate] = useState(null); // ISO string or null

  // Fetch a range and merge into the local cache. Any legacy row that
  // has checked slots without a frozen `ideal_time` gets migrated in
  // place (see medUtils.migrateFreeze) and persisted so the freeze
  // sticks across reloads.
  const migratedRef = useRef(new Set()); // dates already backfilled this session
  const loadRange = useCallback(async (start, end) => {
    try {
      const list = await fetchMedDays(start, end);
      const toPersist = [];
      const merged = list.map((d) => {
        const { day, changed } = migrateFreeze(d);
        if (changed && !migratedRef.current.has(day.date)) {
          migratedRef.current.add(day.date);
          toPersist.push(day);
        }
        return day;
      });
      setDays((prev) => {
        const next = { ...prev };
        for (const d of merged) next[d.date] = d;
        return next;
      });
      // Fire-and-forget persistence for the backfills.
      for (const d of toPersist) {
        try { await upsertMedDay(d.date, d); } catch (_e) { /* silent */ }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('med fetch failed', e);
    }
  }, []);

  // Range used for the Daily View: last ~100 days up to today.
  const loadDailyRange = useCallback(() => {
    const end = todayIso;
    const start = (() => {
      const [y, m, d] = todayIso.split('-').map(Number);
      const s = new Date(y, m - 1, d - 100);
      const yy = s.getFullYear();
      const mm = String(s.getMonth() + 1).padStart(2, '0');
      const dd = String(s.getDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    })();
    return loadRange(start, end);
  }, [todayIso, loadRange]);
  // Save health: null = all saved, 'unsaved' = a save failed (network),
  // 'expired' = the sign-in session is gone (every save 401s). Edits are
  // mirrored to localStorage until the server confirms them, so nothing
  // is lost either way — but the user must be TOLD, loudly.
  const [saveStatus, setSaveStatus] = useState(null);
  const userId = user?.user_id || '';
  // Green "Saved ✓" pulse next to the clock — set to the server-confirm
  // time on every successful PUT, auto-hidden after 4 s.
  const [savedAt, setSavedAt] = useState(null);
  const savedTimer = useRef(null);
  const flashSaved = useCallback(() => {
    setSavedAt(nowEasternPretty());
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedAt(null), 4000);
  }, []);
  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  // Replay any checks that never reached the server (tab closed before
  // the debounced save, network drop, expired session). Runs after the
  // server range is loaded so a newer edit made elsewhere (phone) wins.
  const replayBackup = useCallback(async () => {
    if (!userId) return;
    const backup = readBackup(userId);
    const isos = Object.keys(backup);
    if (!isos.length) return;
    let failed = false;
    for (const iso of isos) {
      const local = backup[iso];
      const server = daysRef.current[iso];
      if (server?.updated_at && local?.saved_at && server.updated_at > local.saved_at) {
        clearBackup(userId, iso); // server copy is newer — keep it
        continue;
      }
      try {
        const saved = await upsertMedDay(iso, local);
        clearBackup(userId, iso);
        flashSaved();
        setDays((prev) => ({ ...prev, [iso]: { ...(saved || local), date: iso } }));
      } catch (e) {
        failed = true;
        setSaveStatus(e?.response?.status === 401 || e?.response?.status === 403 ? 'expired' : 'unsaved');
      }
    }
    if (!failed) setSaveStatus(null);
  }, [userId, flashSaved]);

  useEffect(() => {
    (async () => {
      await loadDailyRange();
      await replayBackup();
    })();
  }, [loadDailyRange, replayBackup]);

  // Keep the Google session alive while the tab stays open (the server
  // slides expiry 7 days on every /auth/me) and surface an expired
  // session immediately instead of letting saves fail in silence.
  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      const ok = await keepSessionAlive();
      if (cancelled) return;
      if (!ok) setSaveStatus('expired');
      else if (saveStatus === 'expired') replayBackup();
    };
    ping();
    const id = setInterval(ping, 30 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [replayBackup, saveStatus]);

  // Retry unsaved rows every minute while something is pending.
  useEffect(() => {
    if (!saveStatus) return undefined;
    const id = setInterval(() => { replayBackup(); }, 60 * 1000);
    return () => clearInterval(id);
  }, [saveStatus, replayBackup]);

  // Range used for the Calendar: the visible grid, including the
  // leading previous-month days (weekday padding on the desktop 7-col
  // grid) and the trailing next-month days (row-fill padding on both
  // grids). Without this widening, those out-of-month cells never
  // get their notes/checks loaded on the current month view, so a
  // day that already has a note shows an empty circle on refresh
  // and only lights up after navigating to that month. See MedCalendar
  // .buildMonthGrid for the exact padding rules — leading is at most
  // 6 days (Sat start), trailing is at most 6 days (either 7-col
  // remainder or 6-col remainder).
  useEffect(() => {
    const firstOfMonth = new Date(year, month, 1);
    const startWeekday = firstOfMonth.getDay(); // 0..6, matches desktop leading
    const gridStart = new Date(year, month, 1 - startWeekday);
    const lastDay = new Date(year, month + 1, 0).getDate();
    // +7 safely covers both the desktop 7-col trailing (≤6) and the
    // mobile 6-col trailing (≤5) without needing to reproduce the
    // remainder math here.
    const gridEnd = new Date(year, month, lastDay + 7);
    const iso = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    loadRange(iso(gridStart), iso(gridEnd));
  }, [year, month, loadRange]);

  // Refresh-on-return: when this tab regains focus (either the
  // Page Visibility API fires visibilitychange back to "visible",
  // or the window fires "focus" / "pageshow" — different browsers
  // and phones behave differently), the user has been looking at
  // other tabs and needs fresh data. Without this, the phone tab
  // shows the state from hours ago and she checks off meds she
  // already logged elsewhere. See spec in this file's header.
  //
  // Order matters:
  //   1) Flush any pending debounced save so our in-flight local
  //      edits hit the server BEFORE we refetch — otherwise the
  //      refetch would overwrite them with the older server copy.
  //   2) Recompute the ET clock and logical today (4 AM rollover
  //      may have happened while the tab was hidden).
  //   3) Refetch both the daily-view range and the calendar range.
  const saveTimersRef = useRef(null); // set below after saveTimers exists
  const refreshOnReturn = useCallback(async () => {
    // (1) flush pending saves
    const timers = saveTimersRef.current;
    if (timers) {
      const pending = [];
      for (const iso of Object.keys(timers)) {
        if (timers[iso]) {
          clearTimeout(timers[iso]);
          timers[iso] = null;
          const doc = daysRef.current[iso];
          if (doc) {
            pending.push(
              upsertMedDay(iso, doc)
                .then(() => { clearBackup(userId, iso); flashSaved(); })
                .catch((e) => {
                  const st = e?.response?.status;
                  setSaveStatus(st === 401 || st === 403 ? 'expired' : 'unsaved');
                }),
            );
          }
        }
      }
      if (pending.length) await Promise.all(pending);
    }
    // (2) refresh clock + logical today
    const nextToday = logicalTodayIso();
    setTodayIso(nextToday);
    setNowPretty(nowEasternPretty());
    setDatePretty(nowEasternDatePretty());
    // (3) refetch ranges. The calendar effect above already reruns
    // whenever year/month/loadRange changes; here we just kick the
    // daily range plus the current calendar grid explicitly so a
    // return-to-tab always pulls fresh server state even when the
    // month hasn't changed.
    const firstOfMonth = new Date(year, month, 1);
    const startWeekday = firstOfMonth.getDay();
    const gridStart = new Date(year, month, 1 - startWeekday);
    const lastDay = new Date(year, month + 1, 0).getDate();
    const gridEnd = new Date(year, month, lastDay + 7);
    const iso = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    await Promise.all([
      loadDailyRange(),
      loadRange(iso(gridStart), iso(gridEnd)),
    ]);
  }, [year, month, loadDailyRange, loadRange, userId, flashSaved]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshOnReturn();
    };
    const onFocus = () => refreshOnReturn();
    // pageshow fires on iOS Safari bfcache restores (opening the tab
    // switcher and coming back), which visibilitychange sometimes
    // does not on that platform.
    const onPageShow = (e) => {
      if (e.persisted || document.visibilityState === 'visible') refreshOnReturn();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [refreshOnReturn]);

  // Keep URL in sync with active view.
  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set('view', view);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Persistence: debounced upsert whenever a day changes.
  const saveTimers = useRef({}); // { [iso]: timeoutId }
  // Expose the same ref object to refreshOnReturn so it can flush
  // pending saves before refetching (see refreshOnReturn above).
  saveTimersRef.current = saveTimers.current;
  const saveDay = useCallback((iso, payload) => {
    // Optimistic update + on-device mirror BEFORE the network call.
    setDays((prev) => ({ ...prev, [iso]: { ...payload, date: iso } }));
    writeBackup(userId, iso, payload);
    if (saveTimers.current[iso]) clearTimeout(saveTimers.current[iso]);
    saveTimers.current[iso] = setTimeout(async () => {
      saveTimers.current[iso] = null;
      try {
        await upsertMedDay(iso, payload);
        clearBackup(userId, iso);
        flashSaved();
        setSaveStatus((prev) => (prev && Object.keys(readBackup(userId)).length ? prev : null));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('med save failed', e);
        const st = e?.response?.status;
        setSaveStatus(st === 401 || st === 403 ? 'expired' : 'unsaved');
      }
    }, 400);
  }, [userId, flashSaved]);

  const todayDay = days[todayIso] || emptyDay(todayIso);
  const pastDays = useMemo(() => {
    return Object.values(days)
      .filter((d) => d && d.date && d.date < todayIso)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [days, todayIso]);

  // Fully-taken set for the calendar (any cached day for the current user).
  const fullyTakenSet = useMemo(() => {
    const s = new Set();
    for (const d of Object.values(days)) {
      if (isFullyTaken(d)) s.add(d.date);
    }
    return s;
  }, [days]);

  // Handlers.
  const handleChangeToday = useCallback((next) => {
    saveDay(todayIso, next);
  }, [todayIso, saveDay]);
  const handleEditPast = useCallback((iso) => setModalDate(iso), []);
  const handleSelectCalendarDay = useCallback((iso) => setModalDate(iso), []);
  const handleOpenNotes = useCallback((iso) => setNotesDate(iso), []);
  const handleModalSave = useCallback(async (next) => {
    saveDay(next.date, next);
  }, [saveDay]);
  const handleModalClose = useCallback(() => setModalDate(null), []);
  const handleNotesSave = useCallback(async (text) => {
    const iso = notesDate;
    if (!iso) return;
    const current = days[iso] || emptyDay(iso);
    const next = { ...current, note: text || '' };
    saveDay(iso, next);
  }, [notesDate, days, saveDay]);
  const handleNotesClose = useCallback(() => setNotesDate(null), []);

  const modalDay = modalDate
    ? (days[modalDate] || emptyDay(modalDate))
    : null;

  return (
    <div className="min-h-screen min-h-[100svh] bg-white text-slate-900" data-testid="med-app">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {saveStatus && (
          <div
            role="alert"
            data-testid="med-save-warning"
            className="mb-4 rounded-lg border-2 border-red-500 bg-red-50 text-red-900 px-4 py-3 text-sm sm:text-base font-semibold flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <span className="flex-1">
              {saveStatus === 'expired'
                ? 'Your sign-in expired — checks are NOT being saved to the server. They are kept on this device and will be saved as soon as you sign in again.'
                : 'Your latest checks have not reached the server yet (connection problem). They are kept on this device and will retry automatically.'}
            </span>
            {saveStatus === 'expired' && (
              <Button
                onClick={onSignInAgain}
                className="bg-red-600 hover:bg-red-700 text-white font-bold"
                data-testid="med-save-warning-signin"
              >
                Sign in again
              </Button>
            )}
          </div>
        )}
        {/* Top bar */}
        <div className="flex items-center justify-between mb-2 gap-2">
          <div
            role="tablist"
            aria-label="Med tracker tabs"
            className="inline-flex rounded-lg border border-slate-300 bg-slate-100 p-1"
            data-testid="med-tabs"
          >
            <TabButton
              active={view === 'calendar'}
              onClick={() => setView('calendar')}
              testId="med-tab-calendar"
            >
              Calendar
            </TabButton>
            <TabButton
              active={view === 'daily'}
              onClick={() => setView('daily')}
              testId="med-tab-daily"
            >
              Daily View
            </TabButton>
          </div>

          {/* Right group: Advanced Settings sits next to Sign out with a
              small gap between the two. Advanced Settings uses the same
              pill styling as the tabs so it reads as part of the same
              button family. */}
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-lg border border-slate-300 bg-slate-100 p-1">
              <TabButton
                active={view === 'settings'}
                onClick={() => setView('settings')}
                testId="med-tab-settings"
              >
                Advanced Settings
              </TabButton>
            </div>
            <button
              type="button"
              onClick={onSignOut}
              className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
              data-testid="med-app-signout"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* ET clock */}
        <div
          className="mb-6 text-xs text-slate-500"
          data-testid="med-et-clock"
        >
          <span className="font-semibold text-slate-700 tabular-nums">{nowPretty}</span>
          <span className="mx-1">·</span>
          <span>{datePretty}</span>
          {savedAt && !saveStatus && (
            <span
              className="ml-3 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 px-2 py-0.5 text-[11px] sm:text-xs font-semibold text-emerald-700 animate-in fade-in duration-200"
              data-testid="med-saved-indicator"
              aria-live="polite"
            >
              Saved ✓ <span className="font-normal text-emerald-600 tabular-nums">{savedAt}</span>
            </span>
          )}
        </div>

        {view === 'calendar' ? (
          <MedCalendar
            year={year}
            month={month}
            onChangeMonth={(y, m) => setYm({ year: y, month: m })}
            fullyTakenSet={fullyTakenSet}
            daysByIso={days}
            onSelectDay={handleSelectCalendarDay}
            onOpenNotes={handleOpenNotes}
            todayIso={todayIso}
          />
        ) : view === 'settings' ? (
          <div
            className="w-full max-w-3xl mx-auto text-center text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-lg p-8 bg-white"
            data-testid="med-settings-view"
          >
            <div className="font-semibold text-slate-700 mb-1">Advanced Settings</div>
            <p>Settings content coming next.</p>
          </div>
        ) : (
          <MedDailyView
            todayIso={todayIso}
            todayDay={todayDay}
            pastDays={pastDays}
            onChangeToday={handleChangeToday}
            onEditPast={handleEditPast}
          />
        )}
      </div>

      {modalDay && (
        <MedDayEditModal
          day={modalDay}
          onSave={handleModalSave}
          onClose={handleModalClose}
        />
      )}

      {notesDate && (
        <MedNotesModal
          date={notesDate}
          initialNote={(days[notesDate]?.note) || ''}
          onSave={handleNotesSave}
          onClose={handleNotesClose}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'px-4 py-1.5 rounded-md text-sm font-semibold transition-colors',
        active
          ? 'bg-slate-900 text-white shadow-md'
          : 'text-slate-700 hover:bg-white hover:text-slate-900',
      ].join(' ')}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
